import { useEffect, useRef } from "react";

interface IUseDynamicTabProps {
    title?: string;
    favicon?: string;
    changeEvery?: number;
    event?: "visibilitychange" | "timeout" | "hiddenTimeout";
    extraConditions?: () => boolean;
}

export const useDynamicTab = ({
    title,
    favicon,
    changeEvery,
    event = "visibilitychange",
    extraConditions = () => true,
}: IUseDynamicTabProps) => {
    const faviconElements = useRef<HTMLCollectionOf<HTMLLinkElement> | null>(
        null,
    );
    const oldFavicon = useRef<string | null>(null);
    const oldTitle = useRef<string | null>(null);
    const getFaviconElements = () =>
        document.getElementsByClassName(
            "favicon",
        ) as HTMLCollectionOf<HTMLLinkElement>;

    const events = {
        timeout: () => {
            if (extraConditions()) {
                window.setInterval(() => {
                    if (
                        faviconElements.current &&
                        faviconElements.current.length
                    ) {
                        handleChangeFavicon(favicon);
                        handleChangeTitle(title);
                    } else {
                        handleChangeFavicon(oldFavicon.current);
                        handleChangeTitle(oldTitle.current);
                    }
                }, changeEvery ?? 1000);
            }
        },
        visibilitychange: () => {
            if (extraConditions()) {
                if (faviconElements.current && faviconElements.current.length) {
                    if (document.visibilityState === "hidden") {
                        handleChangeFavicon(favicon);
                        handleChangeTitle(title);
                    } else {
                        handleChangeFavicon(oldFavicon.current);
                        handleChangeTitle(oldTitle.current);
                    }
                }
            }
        },
        hiddenTimeout: () => {
            if (extraConditions()) {
                window.setInterval(() => {
                    // toggle title and favicon if hidden
                    if (document.visibilityState === "hidden") {
                        // toggle favicon
                        if (faviconElements.current) {
                            [...faviconElements.current].forEach((item) => {
                                if (item.href === oldFavicon.current)
                                    handleChangeFavicon(favicon);
                                else handleChangeFavicon(oldFavicon.current);
                            });
                        }
                        // toggle title
                        if (document.title === oldTitle.current)
                            handleChangeTitle(title);
                        else handleChangeTitle(oldTitle.current);
                    } else {
                        // reset title and favicon if shown
                        handleChangeFavicon(oldFavicon.current);
                        handleChangeTitle(oldTitle.current);
                    }
                }, changeEvery ?? 1000);
            }
        },
    };

    const handleChangeFavicon = (currentFavicon: string | null | undefined) => {
        if (
            faviconElements.current &&
            faviconElements.current.length &&
            currentFavicon
        ) {
            [...faviconElements.current].forEach((item) => {
                item.href = currentFavicon;
            });
        }
    };

    const handleChangeTitle = (currentTitle: string | undefined | null) => {
        if (currentTitle) document.title = currentTitle;
    };

    useEffect(() => {
        faviconElements.current = getFaviconElements();
        oldFavicon.current = faviconElements.current[0].href;
        oldTitle.current = document.title;
        if (document.visibilityState === "hidden") events[event]();
        if (event === "visibilitychange" || event === "hiddenTimeout") {
            window.addEventListener("visibilitychange", () => events[event]());
        } else {
            events[event]();
        }

        return () => {
            if (event === "visibilitychange" || event === "hiddenTimeout")
                window.removeEventListener("visibilitychange", () =>
                    events[event](),
                );
        };
    }, []);
};
